import React, {useCallback, useEffect, useMemo} from "react";
import classNames from "classnames";
import {connect} from "react-redux";
import {Tooltip} from "@material-ui/core";
import {
  getProductIdentifierFrom,
  isSameProductImageOf,
  getProductImageSearchResultColumns, getInterferometricStackDetailsResultColumns,
} from "../../../helpers/productHelper";
import {ProductImageStacType, SearchResultElementType, SearchResultsType} from "../../../types/product";
import {ConfigurationsType} from "../../../types/configurations";
import {
  updateFormDataPayloadAction,
  removeSearchResultDetailsAction,
  storeSearchResultDetailsAction,
  setCurrentMasterItemAction
} from "../../../state/application/actions";
import {
  canQuicklookPreviewBeLoaded,
  getQuicklookPreviewInformationFrom,
  toggleQuicklookPreviewFrom
} from "../../../helpers/quicklookPreviewHelper";
import {couldBeAlreadyInTheCart, IsAlreadyInTheCart} from "../../../helpers/cartHelper";
import InputCheckbox from "../inputs/InputCheckbox";
import {
  getSearchFormValuesByState,
} from "../../../helpers/configurationHelper";
import {
  openSearchResultDetailsDispatchAction,
  updateSelectedProductsDispatchAction
} from "../../../state/product/dispatchActions";
import {updateQuicklookPreviewsAction} from "../../../state/geoMap/actions";
import SearchResultDataProperties from "./shared/SearchResultDataProperties";
import {ShapeType} from "../../../types/shape";
import {isInterferometricStackSearchResults} from "../../../helpers/searchResultsHelper";
import {AllQuicklookPreviewsType} from "../../../state/geoMap/types";
import {DEFAULT_TOOLTIP_PROPS} from "../../../helpers/tooltipHelper";
import translations from "../../../translations";
import QuicklookPreview from "../QuicklookPreview";
import loadingImageUrl from "../../../images/loading.gif";

interface PropsType {
  searchResultID: string,
  item: ProductImageStacType,
  configurations: ConfigurationsType,
  searchFormValues: any,
  selectedProducts: ProductImageStacType[],
  isListViewActive: boolean,
  allQuicklookPreviews: AllQuicklookPreviewsType,
  searchResultDetails: ProductImageStacType,
  ordersSubmitted: any[],
  storeSearchResultDetailsActionHandler: (item: SearchResultElementType) => {},
  removeSearchResultDetailsActionHandler: () => {},
  openSearchResultDetailsActionHandler: any,
  updateQuicklookPreviewsActionHandler: any,
  detailsVersion: boolean,
  selectedArea: ShapeType,
  searchResults: SearchResultsType,
  updateSelectedProductsActionHandler: (
    configurations: ConfigurationsType,
    searchFormValues: any,
    selectedArea: ShapeType,
    isInterferometricStackSearch: boolean,
    items:ProductImageStacType[],
    isChecked: boolean,
    language: string
  ) => {},
  language: string,
  updateFormDataPayloadActionHandler: any,
  formData: any,
  setCurrentMasterItemActionHandler: any
}

const Product = ({
  searchResultID,
  item,
  isListViewActive,
  storeSearchResultDetailsActionHandler,
  removeSearchResultDetailsActionHandler,
  openSearchResultDetailsActionHandler,
  updateSelectedProductsActionHandler,
  selectedProducts,
  allQuicklookPreviews,
  searchResultDetails,
  searchResults,
  detailsVersion,
  ordersSubmitted,
  configurations,
  selectedArea,
  searchFormValues,
  updateQuicklookPreviewsActionHandler,
  language,
  updateFormDataPayloadActionHandler,
  formData,
  setCurrentMasterItemActionHandler
}: PropsType) => {
  const isInterferometricStackSearch = isInterferometricStackSearchResults(searchResults);
  const couldProductImageBeAlreadyInTheCart = couldBeAlreadyInTheCart(ordersSubmitted, item);
  const isProductImageAlreadyInTheCart = IsAlreadyInTheCart(configurations, ordersSubmitted, item);
  const isProductSelected = selectedProducts.some(isSameProductImageOf(item));
  const isShowProductDetailsButtonEnabled = true;
  const isCenteringButtonEnabled = true;
  const {
    productImageQuicklookPreview,
    isQuicklookPreviewAvailable,
    isQuicklookPreviewShownInMap
  } = getQuicklookPreviewInformationFrom(allQuicklookPreviews, item);

  const isBaselineFilterMasterItem = !!item?.isMasterItem;

  const isMaster: boolean = useMemo(
    () => (formData.masterId === item.id),
    [formData.masterId, item.id]
  );

  const hasFocusSelected: boolean = useMemo(() => searchResultDetails &&
  getProductIdentifierFrom(searchResultDetails) === getProductIdentifierFrom(item),
  [item, searchResultDetails]);

  const changeSelectedState = useCallback((value: boolean): void => {
    updateSelectedProductsActionHandler(
      configurations,
      searchFormValues,
      selectedArea,
      isInterferometricStackSearch,
      [item],
      value,
      language
    );

    if (isMaster && !value) {
      // Master can't be not-selected
      updateFormDataPayloadActionHandler({
        masterId: null
      });
    }
  }, [configurations, isInterferometricStackSearch, isMaster, item, language, searchFormValues, selectedArea, updateFormDataPayloadActionHandler, updateSelectedProductsActionHandler])

  /** Select/Deselect item as slave */
  const toggleAsSelected = useCallback((isSelected: boolean) => {
    changeSelectedState(isSelected);

    if (isSelected) return;

    if (productImageQuicklookPreview && isQuicklookPreviewShownInMap) {
      updateQuicklookPreviewsActionHandler([
        toggleQuicklookPreviewFrom(productImageQuicklookPreview, false)
      ]);
    }

    if (hasFocusSelected) {
      removeSearchResultDetailsActionHandler();
    }
  }, [changeSelectedState, hasFocusSelected, isQuicklookPreviewShownInMap, productImageQuicklookPreview, removeSearchResultDetailsActionHandler, updateQuicklookPreviewsActionHandler])

  const selectAsMaster = useCallback(() => {
    updateFormDataPayloadActionHandler({
      masterId: !isMaster ? item.id : null
    });

    setCurrentMasterItemActionHandler(item);

    // Master
    toggleAsSelected(true);
  }, [isMaster, item, setCurrentMasterItemActionHandler, toggleAsSelected, updateFormDataPayloadActionHandler]);

  /** Select MasterItem/BaselineFilterMasterItem */
  useEffect(() => {
    if ((isBaselineFilterMasterItem || isMaster) && !isProductSelected) {
      toggleAsSelected(true);
    }
  }, [isBaselineFilterMasterItem, isMaster, isProductSelected, toggleAsSelected]);

  const onChangeInputCheckbox = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Master can't be deselected during baseline-filtering
    if (isBaselineFilterMasterItem) return;
    const isSelected = e.target.checked;

    toggleAsSelected(isSelected);
  }, [isBaselineFilterMasterItem, toggleAsSelected]);

  const inputCheckboxTooltip: string = useMemo(() => {
    if (isProductImageAlreadyInTheCart) { return translations("alreadyInCartProduct", language) }
    if (couldProductImageBeAlreadyInTheCart) { return translations("couldAlreadyInCartProduct", language) }
    if (isProductSelected) { return translations("deselectThisProduct", language) }
    return translations("selectThisProduct", language);
  }, [language, isProductImageAlreadyInTheCart, couldProductImageBeAlreadyInTheCart, isProductSelected]);

  const onClickQuicklookButton = useCallback(() => {
    const toggle = !isQuicklookPreviewShownInMap;
    if (isQuicklookPreviewAvailable && productImageQuicklookPreview) {
      if (toggle) {
        changeSelectedState(toggle);
      }
      updateQuicklookPreviewsActionHandler([
        toggleQuicklookPreviewFrom(productImageQuicklookPreview, toggle)
      ]);
    }
  }, [
    changeSelectedState,
    isQuicklookPreviewAvailable,
    isQuicklookPreviewShownInMap,
    productImageQuicklookPreview,
    updateQuicklookPreviewsActionHandler
  ]);

  const onClickCenteringButton = useCallback(() => {
    if (!isProductSelected) {
      changeSelectedState(true);
    }
    if (hasFocusSelected) {
      removeSearchResultDetailsActionHandler();
    } else {
      storeSearchResultDetailsActionHandler(item);
    }
  }, [
    changeSelectedState,
    hasFocusSelected,
    isProductSelected,
    item,
    removeSearchResultDetailsActionHandler,
    storeSearchResultDetailsActionHandler
  ]);

  return (
    <div
      key={`product-${searchResultID}`}
      className={classNames("search-result", {
        "col-12 list-view": isListViewActive,
        "col-6 matrix-view": !isListViewActive,
        "product-master": isBaselineFilterMasterItem,
      })}
    >
      <div className="position-relative d-flex inner-wrapper">
        <div className="wrapper-select-search-result-checkbox">
          <InputCheckbox
            name={`imgCheckbox_${searchResultID}`}
            checked={isProductSelected}
            tooltip={inputCheckboxTooltip}
            onChange={onChangeInputCheckbox}
            checkboxClassName={classNames({
              "previously-selected": couldProductImageBeAlreadyInTheCart
            })}
            disabled={isProductImageAlreadyInTheCart || isBaselineFilterMasterItem}
          />
        </div>

        <QuicklookPreview
          productImage={item}
          className="product-photo"
        />

        <div className="search-result-data">
          <SearchResultDataProperties
            properties={detailsVersion
              ? getInterferometricStackDetailsResultColumns(configurations, item, language)
              : getProductImageSearchResultColumns(configurations, item, language)}
            isListViewActive={isListViewActive}
          />

          <div className="product-action-buttons-container-parent">
            <div className="product-action-buttons-container">
              <Tooltip
                {...DEFAULT_TOOLTIP_PROPS}
                title={translations("detailsProduct", language)}
                placement="top"
              >
                <span>
                  <button
                    type="button"
                    className="remove-btn-defaults"
                    disabled={!isShowProductDetailsButtonEnabled}
                    style={!isShowProductDetailsButtonEnabled ? {pointerEvents: "none"} : {}}
                    onClick={() => openSearchResultDetailsActionHandler(item)}
                  >
                    <svg><use xlinkHref="#product-list-icon" fill="#ffffff" /></svg>
                  </button>
                </span>
              </Tooltip>

              <Tooltip
                {...DEFAULT_TOOLTIP_PROPS}
                title={isQuicklookPreviewAvailable ? translations("previewProduct", language) : translations("previewNotAvailableProduct", language)}
                placement="top"
              >
                <span>
                  <button
                    type="button"
                    className={classNames("quicklook-preview-button remove-btn-defaults", {
                      selected: isQuicklookPreviewShownInMap
                    })}
                    style={!isQuicklookPreviewAvailable ? {pointerEvents: "none"} : {}}
                    disabled={!isQuicklookPreviewAvailable}
                    onClick={onClickQuicklookButton}
                  >
                    {!canQuicklookPreviewBeLoaded(searchFormValues) || !!productImageQuicklookPreview
                      ? <svg><use xlinkHref="#image-icon" fill="#ffffff" /></svg>
                      : <img className="loading-spinner-img" src={loadingImageUrl} alt="loading..." />}
                  </button>

                </span>
              </Tooltip>

              {!detailsVersion && (
                <Tooltip
                  {...DEFAULT_TOOLTIP_PROPS}
                  title={translations("centeringProduct", language)}
                  placement="top"
                >
                  <span>
                    <button
                      type="button"
                      className={classNames("focus-button remove-btn-defaults", {
                        selected: hasFocusSelected
                      })}
                      style={!isCenteringButtonEnabled ? {pointerEvents: "none"} : {}}
                      disabled={!isCenteringButtonEnabled}
                      onClick={onClickCenteringButton}
                    >
                      <svg><use xlinkHref="#targer-icon" fill="#ffffff" /></svg>
                    </button>
                  </span>
                </Tooltip>
              )}
            </div>

            <div className="product-action-buttons-container">
              <span>
                <button
                  type="button"
                  className={classNames("baseline-filter-button remove-btn-defaults", {
                    selected: isMaster || isBaselineFilterMasterItem
                  })}
                  onClick={selectAsMaster}
                >
                  Master
                </button>
              </span>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
};

const mapStateToProps = (state) => ({
  isListViewActive: state.product.isListViewActive,
  selectedProducts: state.product.selectedProducts,
  searchResults: state.product.searchResults,
  tabActiveInLeftSidebar: state.application.tabActiveInLeftSidebar,
  configurations: state.application.configurations,
  ordersSubmitted: state.cart.ordersSubmitted,
  searchFormValues: getSearchFormValuesByState(state),
  allQuicklookPreviews: state.geoMap.allQuicklookPreviews,
  selectedArea: state.geoMap.lastShape,
  searchResultDetails: state.application.searchResultDetails,
  language: state.application.appParameters.language,
  formData: state.application.formData
});

const mapDispatchToProps = (dispatch) => ({
  storeSearchResultDetailsActionHandler: (item) => dispatch(storeSearchResultDetailsAction(item)),
  openSearchResultDetailsActionHandler: (item) => dispatch(openSearchResultDetailsDispatchAction(item)),
  removeSearchResultDetailsActionHandler: () => dispatch(removeSearchResultDetailsAction()),
  updateSelectedProductsActionHandler: (configurations, searchFormValues, selectedArea, isInterferometricStackSearch, products, add, language) => dispatch(updateSelectedProductsDispatchAction(configurations, searchFormValues, selectedArea, isInterferometricStackSearch, products, add, language)),
  updateQuicklookPreviewsActionHandler: (quicklookPreviews) => dispatch(updateQuicklookPreviewsAction(quicklookPreviews)),
  updateFormDataPayloadActionHandler: (masterAndSlaveID) => dispatch(updateFormDataPayloadAction(masterAndSlaveID)),
  setCurrentMasterItemActionHandler: (masterItem) => dispatch(setCurrentMasterItemAction(masterItem))
});

export default connect(mapStateToProps, mapDispatchToProps)(Product)
